import React, {
  ChangeEvent,
  forwardRef,
  KeyboardEvent,
  ReactNode,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import clsx from 'clsx';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import styles from './SelectInput.module.scss';
import { CloseImage, DropdownImage, SelectInputSearchImage } from 'static/images';
import useRandomIdFallback from 'hooks/randomIdFallback';
import DropdownContentWithPortal from 'components/DropdownContentWithPortal';
import { KeyCode } from 'common/KeyCode';
import DropdownContent from 'components/SelectInput/DropdownContent';
import { findIndex } from 'lodash';
import CustomBlurInput from 'components/SelectInput/CustomBlurInput';
import LoaderWithState, { LoaderState } from 'components/LoaderWithState/LoaderWithState';
import Spinner from 'components/Spinner';
import useOutOfView from 'hooks/useOutOfView';
import useCombinedRefs from 'hooks/useCombinedRefs';
import ApplicationTag from 'components/Tag/ApplicationTag';
import WrapperWithTooltip from 'components/Tooltip';
import Label from 'components/Label';

export interface Option {
  name: string;
  value: string;
  description?: string;
  disabled?: boolean;
  icon?: ReactNode;
  iconAfter?: ReactNode;
  tooltip?: ReactNode;
  color?: string;
  componentOption?: ReactNode;
}

export interface SelectInputProps<DropdownAnchor extends HTMLElement = HTMLElement>
  extends Omit<React.HTMLProps<HTMLInputElement>, 'value' | 'onChange'> {
  value?: string;
  selectedOption?: Option;
  userInput: string | null;
  onChange: (option: Option) => void;
  onInputChange: (value: string) => void;
  options: Option[];
  labelTitle?: string;
  placeholder?: string;
  id?: string;
  link?: ReactNode;
  hasRightNeighbour?: boolean;
  hasLeftNeighbour?: boolean;
  lightLabel?: boolean;
  optionsListClassName?: string;
  autoscroll?: boolean;
  dropdownAnchorRef?: RefObject<DropdownAnchor>;
  loading?: boolean;
  loaded?: boolean;
  hideClearIcon?: boolean;
  errorMessage?: string;
  hasError?: boolean;
  showLoader?: boolean;
  loaderState?: LoaderState | null;
  onLoaderStateReset?: () => void;
  selectControlClassName?: string;
  isLabelTag?: boolean;
  tooltip?: string;
  selectInputClassName?: string;
  inputIcon?: React.ReactNode;
  required?: boolean;
  onBlur?: () => void;
  inputValueContainerClassName?: string;
  forceValidate?: (value: string) => void;
}

const BLUR_DELAY_TIME = 10;
const HIDDEN_TEXT_PADDING = 26;

const SelectInput = forwardRef<HTMLInputElement, SelectInputProps>(
  (
    {
      selectedOption,
      userInput,
      onChange,
      onFocus,
      onInputChange,
      options,
      labelTitle,
      id: providedId,
      placeholder = selectedOption ? selectedOption.name : '',
      readOnly = false,
      disabled = false,
      hasRightNeighbour = false,
      hasLeftNeighbour = false,
      link,
      lightLabel,
      optionsListClassName,
      autoscroll = true,
      dropdownAnchorRef,
      className,
      loading = false,
      loaded = true,
      hideClearIcon = false,
      onBlur,
      errorMessage,
      hasError = false,
      showLoader,
      loaderState,
      onLoaderStateReset,
      tabIndex,
      selectControlClassName,
      isLabelTag,
      tooltip,
      selectInputClassName,
      inputIcon,
      required,
      forceValidate,
      inputValueContainerClassName,
    },
    ref,
  ) => {
    const id = useRandomIdFallback(providedId);
    const selectedOptionIndex = findIndex(options, selectedOption);
    const [itemIndexToFocus, setItemIndexToFocus] = useState<number | undefined>(selectedOptionIndex);
    const [hasFocus, setHasFocus] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const [focusedItemIndex, setFocusedItemIndex] = useState<number>(selectedOptionIndex);
    const [currentInputWidth, setCurrentInputWidth] = useState(0);

    const inputClassName = clsx({
      [styles.selectControl]: true,
      [styles.readOnlySelectControl]: readOnly,
      [styles.disabledSelectControl]: disabled,
      [styles.selectControlWithRightNeighbour]: hasRightNeighbour,
      [styles.selectControlWithLeftNeighbour]: hasLeftNeighbour,
      [styles.inputFocus]: hasFocus,
      [styles.highlightSelectControl]: errorMessage || hasError,
    });
    const labelRef = useRef<HTMLLabelElement>(null);
    const inputRef = useCombinedRefs(ref, useRef<HTMLInputElement>(null));
    const hiddenTextElement = useRef<HTMLParagraphElement>(null);
    const mainRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      setIsOpen(typeof userInput === 'string' && !!loaded);
    }, [userInput, loaded]);

    const handleOptionSelect = ({ value: valueToSelect, disabled: isOptionDisabled }: Option) => {
      if (isOptionDisabled) {
        return;
      }
      setHasFocus(false);
      setIsOpen(false);
      const option = options.find(({ value: optionValue }) => optionValue === valueToSelect)!;
      onChange(option);
    };

    const renderDropdownContent = () => (
      <DropdownContent
        options={options}
        itemIndexToFocus={itemIndexToFocus}
        setFocusedItemIndex={setFocusedItemIndex}
        handleSelect={handleOptionSelect}
        autoscroll={autoscroll}
        selectedValue={selectedOption?.value}
        focusedItemIndex={focusedItemIndex}
        containerClassName={optionsListClassName}
        isLabelTag={isLabelTag}
        ref={dropdownRef}
      />
    );

    const dropdownHandlerRef = dropdownAnchorRef || ((labelRef as unknown) as RefObject<HTMLElement>);

    const renderDropdown = () => (
      <DropdownContentWithPortal dropdownRef={dropdownRef} handlerRef={dropdownHandlerRef}>
        {renderDropdownContent()}
      </DropdownContentWithPortal>
    );

    const getActiveItemIndex = (eventType: KeyCode.ArrowUp | KeyCode.ArrowDown) => {
      const maxItemIndex = options.length - 1;
      if (eventType === KeyCode.ArrowDown) {
        return focusedItemIndex < maxItemIndex ? focusedItemIndex + 1 : 0;
      }
      return focusedItemIndex <= 0 ? maxItemIndex : focusedItemIndex - 1;
    };

    const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
      const { keyCode } = event;
      if (keyCode === KeyCode.ArrowDown && !isOpen && hasFocus) {
        setIsOpen(true);
        return;
      }

      if (keyCode === KeyCode.ArrowDown || keyCode === KeyCode.ArrowUp) {
        const itemIndexToSet = getActiveItemIndex(keyCode);
        setFocusedItemIndex(itemIndexToSet);
        setItemIndexToFocus(itemIndexToSet);
      }

      if (keyCode === KeyCode.Esc) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
      const { keyCode } = event;

      if (keyCode === KeyCode.Enter && !!options[focusedItemIndex]) {
        setHasFocus(false);
        setIsOpen(false);
        event.currentTarget.blur();
        onChange(options[focusedItemIndex]);
      }
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      if (readOnly) {
        return;
      }

      setHasFocus(true);

      onFocus?.(event);
    };

    const inputContainerClassName = clsx({
      [styles.inputContainer]: true,
      [styles.inputContainerWithRightNeighbour]: hasRightNeighbour,
      [styles.inputContainerWithLeftNeighbour]: hasLeftNeighbour,
      [styles.selectInputWithValue]: selectedOption || userInput,
    });

    const getInputValue = () => {
      if (hasFocus) {
        return userInput || '';
      }
      return selectedOption ? selectedOption.name : '';
    };

    useLayoutEffect(() => {
      if (selectedOption?.iconAfter && !hasFocus) {
        const { offsetWidth } = hiddenTextElement.current!;
        setCurrentInputWidth(offsetWidth);
      }
    }, [getInputValue()]);

    const blurInputWithDelay = () => {
      setTimeout(() => {
        inputRef.current!.blur();
        setHasFocus(false);
      }, BLUR_DELAY_TIME);
    };

    const handleInputClick = () => {
      if (readOnly || (!!userInput && isOpen) || !options.length) {
        return;
      }

      const openState = !isOpen;
      if (openState) {
        setItemIndexToFocus(selectedOptionIndex);
      } else if (!userInput) {
        blurInputWithDelay();
      }
      setIsOpen(openState);
    };

    const handleClickOutside = () => {
      if (hasFocus) {
        onBlur?.();
      }

      setIsOpen(false);
      setHasFocus(false);
    };

    const onClear = () => {
      onChange({
        name: '',
        value: '',
      });
      onInputChange('');
      forceValidate?.('');
    };

    const renderInputIcon = () => {
      if (inputIcon) {
        return <div className={styles.customInputIconContainer}>{inputIcon}</div>;
      }

      if (loading) {
        return (
          <div className={styles.inputLoader}>
            <Spinner size={24} />
          </div>
        );
      }

      return (
        <div className={styles.inputIconContainer}>
          {hideClearIcon || readOnly || disabled ? null : (
            <CloseImage className={clsx(styles.clearIcon, hasFocus && styles.showClearIcon)} onClick={onClear} />
          )}
          {isOpen ? <SelectInputSearchImage /> : <DropdownImage />}
        </div>
      );
    };

    useOutOfView(mainRef, () => {
      setIsOpen(false);
    });

    const renderSelectedLabel = () => {
      if (isLabelTag && selectedOption) {
        return <ApplicationTag color={selectedOption.color}>{selectedOption.name}</ApplicationTag>;
      }
      return null;
    };

    return (
      <div className={clsx(inputContainerClassName, className)} ref={mainRef}>
        <div className={styles.headerContainer}>
          <Label htmlFor={id} className={clsx(lightLabel && styles.whiteLabel)} required={required}>
            {labelTitle}
          </Label>
          {link}
        </div>
        <ClickAwayListener onClickAway={() => handleClickOutside()}>
          <div className={styles.selectInputWrapper}>
            <WrapperWithTooltip tooltip={tooltip}>
              <div id={id} className={clsx(styles.select, selectInputClassName)}>
                <label className={clsx(inputClassName, selectControlClassName)} ref={labelRef}>
                  <div className={clsx(isLabelTag ? styles.inputLabelValueContainer : styles.inputValueContainer, inputValueContainerClassName)}>
                    {renderSelectedLabel()}
                    {selectedOption?.icon}
                    <CustomBlurInput
                      ref={inputRef}
                      type="text"
                      value={getInputValue()}
                      placeholder={!isLabelTag ? placeholder : ''}
                      onChange={({ target }: ChangeEvent<HTMLInputElement>) => {
                        onInputChange(target.value);
                      }}
                      onFocus={handleFocus}
                      onClick={() => handleInputClick()}
                      readOnly={readOnly}
                      disabled={disabled}
                      onKeyUp={handleKeyUp}
                      onKeyDown={handleKeyDown}
                      onCustomBlur={handleClickOutside}
                      tabIndex={tabIndex}
                      style={selectedOption?.iconAfter ? { width: currentInputWidth } : undefined}
                      className={clsx(
                        isLabelTag && !hasFocus && styles.emptyInput,
                        isLabelTag && selectedOption && hasFocus && styles.inputWithLabel,
                      )}
                    />
                  </div>
                  {selectedOption?.iconAfter && !hasFocus ? (
                    <>
                      <div
                        className={clsx(styles.iconAfter, hasFocus && styles.hiddenIconAfter)}
                        style={{ left: HIDDEN_TEXT_PADDING + currentInputWidth }}
                      >
                        {selectedOption.iconAfter}
                      </div>
                      <span
                        ref={hiddenTextElement}
                        className={clsx(
                          styles.textLabel,
                          (hasFocus || !selectedOption?.iconAfter) && styles.fullInputWidth,
                        )}
                      >
                        {getInputValue()}
                      </span>
                    </>
                  ) : null}
                  {renderInputIcon()}
                  {showLoader && (
                    <LoaderWithState
                      className={clsx(styles.loader, hideClearIcon && styles.fixedLoader)}
                      state={loaderState}
                      onStateReset={onLoaderStateReset}
                    />
                  )}
                </label>
                {isOpen && loaded && renderDropdown()}
                {errorMessage ? <p className={styles.errorNotification}>{errorMessage}</p> : null}
              </div>
            </WrapperWithTooltip>
          </div>
        </ClickAwayListener>
      </div>
    );
  },
);

export default SelectInput;
